import {
  APIComponentAttributes,
  APIDiffContext,
  APIFieldAttributes,
  APIFieldType,
  APIGlobalField,
  APIScopeDataCore,
  APIReferenceAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  DateRangeFieldMapType,
  Metamodel,
  ScopeDataCollection,
} from '@ardoq/api-types';
import {
  createCombinedDateRangeFieldNames,
  extractDateRangeFieldName,
  fieldNameIsDateRangeStartField,
  fieldNameIsPartOfDateRangeField,
  getDateRangeFieldNamePairsFromFieldNames,
} from './nameUtils';
import {
  createGlobalDateRangeField,
  createDateRangeField,
  getDateRangeFieldsFromGlobalFields,
  getDateRangeFieldsGroupedByModelAndWorkspace,
  getDateRangeFieldsGroupedByModelMap,
  getDateRangeType,
  isDateRangeFieldType,
  isPartOfDateRangeField,
  isDateRangeField,
  toCombinedDateRangeFieldIds,
  toCombinedDateRangeFieldName,
} from './utils';
import {
  extractDateRangeFieldLabel,
  fieldLabelIsDateRangeEndField,
  fieldLabelIsDateRangeStartField,
  fieldLabelIsPartOfDateRangeField,
  getDateRangeFieldLabelPairsFromFieldLabels,
} from './labelUtils';
import { isTruthy } from '@ardoq/common-helpers';
import { uniqBy } from 'lodash';

export const mergeDateRangeFieldsForDiffContext = ({
  response: { master, branch, branchOff, ...rest },
}: {
  response: APIDiffContext;
}) => {
  // Build a unified dateRangeFieldMap from the combined fields of master and
  // branch so that date range entity merging is symmetric. Without this, a date
  // range field created after branchOff would only be merged on the side that
  // has the field definitions, creating phantom diffs.
  const allWorkspaces = uniqBy(
    [...master.workspaces, ...branch.workspaces],
    w => w._id
  );
  const modelWorkspaceMap = new Map(
    allWorkspaces.map(workspace => [workspace.componentModel, workspace._id])
  );
  const allFields = uniqBy(
    [
      ...master[ScopeDataCollection.FIELDS],
      ...branch[ScopeDataCollection.FIELDS],
    ],
    f => f._id
  );
  const unifiedDateRangeFieldMap = getDateRangeFieldsGroupedByModelAndWorkspace(
    allFields,
    modelWorkspaceMap
  );

  return {
    response: {
      master: getMergedDateRangeFieldForScopeData(
        master,
        undefined,
        unifiedDateRangeFieldMap
      ),
      branch: getMergedDateRangeFieldForScopeData(
        branch,
        undefined,
        unifiedDateRangeFieldMap
      ),
      branchOff: getMergedDateRangeFieldForScopeData(
        branchOff,
        [...branch.workspaces, ...master.workspaces],
        unifiedDateRangeFieldMap
      ),
      ...rest,
    },
  };
};

export const getMergedDateRangeFieldForScopeData = <T extends APIScopeDataCore>(
  scopeData: T,
  workspaces?: APIWorkspaceAttributes[],
  entityDateRangeFieldMap?: DateRangeFieldMapType
): T & { dateRangeFieldMap: DateRangeFieldMapType } => {
  const modelWorkspaceMap = new Map(
    (workspaces ?? scopeData.workspaces).map(workspace => [
      workspace.componentModel,
      workspace._id,
    ])
  );

  // The returned dateRangeFieldMap is always built from this scope data's own
  // field definitions so downstream field-definition lookups stay consistent.
  // When an external entityDateRangeFieldMap is provided (diff context), it is
  // used only for entity merging — this can leave entities with merged date
  // range properties that the local map does not know about. The defensive
  // isDateRangeValue fallback in splitUtils.splitFieldsOnEntity handles that
  // mismatch when splitting entities back for the API.
  const dateRangeFieldMap = getDateRangeFieldsGroupedByModelAndWorkspace(
    scopeData[ScopeDataCollection.FIELDS],
    modelWorkspaceMap
  );
  const dateRangeFieldMapForEntities =
    entityDateRangeFieldMap ?? dateRangeFieldMap;
  const { fields } = mergeDateTimeFieldsToDateRangeFields(
    scopeData[ScopeDataCollection.FIELDS]
  );
  const components = scopeData[ScopeDataCollection.COMPONENTS].map(component =>
    mergeDateRangeFieldsForEntity<APIComponentAttributes>(
      component,
      dateRangeFieldMapForEntities
    )
  );
  const references = scopeData[ScopeDataCollection.REFERENCES].map(reference =>
    mergeDateRangeFieldsForEntity<APIReferenceAttributes>(
      reference,
      dateRangeFieldMapForEntities
    )
  );

  return {
    ...scopeData,
    [ScopeDataCollection.COMPONENTS]: components,
    [ScopeDataCollection.REFERENCES]: references,
    [ScopeDataCollection.FIELDS]: fields,
    dateRangeFieldMap,
  };
};

export const mergeDateRangeFieldsForEntity = <
  T extends Partial<APIComponentAttributes> | Partial<APIReferenceAttributes>,
>(
  entity: T,
  dateRangeFieldMap: DateRangeFieldMapType
): T => {
  const entityKeys = new Set(Object.keys(entity));
  const dateRangeFields =
    entity.model && dateRangeFieldMap.get(entity.model)
      ? (dateRangeFieldMap.get(entity.model) ?? {})
      : {};
  const newEntity = { ...entity };

  Object.entries(dateRangeFields).forEach(([fieldName, { start, end }]) => {
    if (entityKeys.has(start.name) && entityKeys.has(end.name)) {
      delete newEntity[start.name];
      delete newEntity[end.name];
      newEntity[fieldName] = {
        start: entity[start.name],
        end: entity[end.name],
      };
    }
  });

  return newEntity;
};

export const mergeDateRangeFieldsByFieldPairs = (
  entity: Record<string, unknown>,
  dateRangePairs: Record<string, { start: string; end: string }>
): Record<string, unknown> => {
  const merged: Record<string, unknown> = { ...entity };
  Object.entries(dateRangePairs).forEach(([fieldName, { start, end }]) => {
    const startValue = entity[start];
    const endValue = entity[end];

    if (startValue !== undefined || endValue !== undefined) {
      merged[fieldName] = {
        start: startValue ?? null,
        end: endValue ?? null,
      };
      delete merged[start];
      delete merged[end];
    }
  });
  return merged;
};

const mergeFields = <T extends APIGlobalField>(
  fields: T[],
  getDateRangeFieldPairFromField: (
    field: T
  ) => { start: T; end: T } | undefined,
  createDateRangeField: (
    dateRangeType: APIFieldType,
    start: T,
    end: T
  ) => T & { dateTimeFields: { start: T; end: T } }
) =>
  fields
    .filter(field => !isDateRangeFieldType(field.type)) // don't accidentally recreate date range fields
    .reduce<{
      fields: T[];
      createdDateRangeFields: Array<
        T & { dateTimeFields: { start: T; end: T } }
      >;
      removedFields: T[];
    }>(
      (acc, field) => {
        const dateRangeFieldPair = getDateRangeFieldPairFromField(field);
        if (!(dateRangeFieldPair?.start && dateRangeFieldPair?.end)) {
          acc.fields.push(field);
          return acc;
        }
        const { start, end } = dateRangeFieldPair;
        if (
          start === field &&
          end // if start field, create date range field here
        ) {
          const dateRangeField = createDateRangeField(
            getDateRangeType(field.type),
            start,
            end
          );

          acc.fields.push(dateRangeField);
          acc.removedFields.push(field);
          acc.createdDateRangeFields.push(dateRangeField);

          return acc;
        }
        // if end field, don't do anything, since the date range field was created when the start field was encountered
        acc.removedFields.push(field);
        return acc;
      },
      { createdDateRangeFields: [], fields: [], removedFields: [] }
    );

// TODO ZhenyaUsenko: rename this and other *dateTime* functions
// as date ranges can now be either dateTime or dateOnly
/**
 * This function takes an array of fields and returns a new array of fields where date range fields are created from date time field pairs.
 * We create date range fields by looking for pairs of fields with the same name, where one ends with '_start_date' and the other with '_end_date'.
 * Additionally, the fields have to have type=APIFieldType.DATE_TIME and be part of the same model.
 */
export const mergeDateTimeFieldsToDateRangeFields = <
  T extends APIFieldAttributes,
>(
  fields: T[]
) => {
  const dateRangeFieldMap = getDateRangeFieldsGroupedByModelMap(fields);
  const getDateRangeFieldPair = (field: T) => {
    const dateRangeFieldsForModel = dateRangeFieldMap.get(field.model);
    const dateRangeFieldName = isPartOfDateRangeField(field)
      ? extractDateRangeFieldName(field.name)
      : undefined;
    return dateRangeFieldName && dateRangeFieldsForModel
      ? (dateRangeFieldsForModel[dateRangeFieldName] as {
          start: T;
          end: T;
        })
      : undefined;
  };

  return mergeFields(
    fields,
    getDateRangeFieldPair,
    createDateRangeField as unknown as (
      dateRangeType: APIFieldType,
      start: T,
      end: T
    ) => T & { dateTimeFields: { start: T; end: T } }
  );
};

/**
 * This function takes an array of fields and returns a new array of fields where date range fields are created from date time field pairs.
 * We create date range fields by looking for pairs of fields with the same name, where one ends with '_start_date' and the other with '_end_date'.
 * For the global fields we don't have a model id, so we match the field pairs only based on name and type. That makes the matching here a bit less
 * strict than in mergeDateTimeFieldsToDateRangeFields()
 */
export const mergeGlobalFieldsToDateRangeFields = (
  globalFields: APIGlobalField[]
) => {
  const dateRangeFields = getDateRangeFieldsFromGlobalFields(globalFields);
  const getDateRangeFieldPair = (field: APIGlobalField) => {
    const dateRangeFieldName = isPartOfDateRangeField(field)
      ? extractDateRangeFieldName(field.name)
      : undefined;
    return dateRangeFieldName ? dateRangeFields[dateRangeFieldName] : undefined;
  };

  return mergeFields(
    globalFields,
    getDateRangeFieldPair,
    createGlobalDateRangeField
  );
};

const mergeDateRangeFieldsInFieldUsage = (
  fieldUsage: Record<string, number>,
  dateRangeFieldMap: DateRangeFieldMapType,
  workspaceId: ArdoqId
) =>
  Object.entries(fieldUsage).reduce<Record<string, number>>(
    (acc, [fieldName, usageNumber]) => {
      if (
        fieldNameIsPartOfDateRangeField(fieldName) &&
        dateRangeFieldMap.get(workspaceId)?.[
          extractDateRangeFieldName(fieldName)
        ]
      ) {
        const dateRangeFieldName = extractDateRangeFieldName(fieldName);
        return {
          ...acc,
          [dateRangeFieldName]:
            (acc[dateRangeFieldName] ?? 0) + usageNumber / 2, // /2 so that a date range field where only the start or end is filled out only counts as half a usage
        };
      }
      return { ...acc, [fieldName]: usageNumber };
    },
    {}
  );

export const foo = (properFields: APIFieldAttributes[]) =>
  mergeDateTimeFieldsToDateRangeFields(properFields).fields.map(field =>
    isDateRangeField(field)
      ? {
          ...field,
          _id: toCombinedDateRangeFieldIds(field),
          name: toCombinedDateRangeFieldName(field),
        }
      : field
  );

export const mergeDateRangeFieldsOnMetamodel = <T extends Metamodel>(
  metamodel: T,
  getFieldsOfWorkspace: (workspaceId: ArdoqId) => APIFieldAttributes[]
): T => {
  const modelIdWorkspaceIdMap = new Map(
    Object.values(metamodel.metamodel.workspaces).map(workspace => [
      workspace.modelId,
      workspace.id,
    ])
  );
  const dateRangeFieldMap = getDateRangeFieldsGroupedByModelAndWorkspace(
    metamodel.workspaceIds.flatMap(getFieldsOfWorkspace),
    modelIdWorkspaceIdMap
  );

  return {
    ...metamodel,
    metamodel: {
      ...metamodel.metamodel,
      dateRangeFieldMap,
      componentTypes: Object.fromEntries(
        Object.entries(metamodel.metamodel.componentTypes).map(
          ([id, componentType]) => [
            id,
            {
              ...componentType,
              metadata: {
                ...componentType.metadata,
                fieldUsage: mergeDateRangeFieldsInFieldUsage(
                  componentType.metadata.fieldUsage,
                  dateRangeFieldMap,
                  componentType.workspaceId
                ),
              },
            },
          ]
        )
      ),
      referenceTypes: Object.fromEntries(
        Object.entries(metamodel.metamodel.referenceTypes).map(
          ([id, referenceType]) => [
            id,
            {
              ...referenceType,
              metadata: {
                ...referenceType.metadata,
                fieldUsage: mergeDateRangeFieldsInFieldUsage(
                  referenceType.metadata.fieldUsage,
                  dateRangeFieldMap,
                  referenceType.workspaceId
                ),
              },
            },
          ]
        )
      ),
    },
  };
};

// You can use this function if you have a list of field labels that you know all belong to the same component/reference type
export const mergeDateRangeFieldLabels = (fieldLabels: string[]): string[] => {
  const dateRangeFieldLabelPairs =
    getDateRangeFieldLabelPairsFromFieldLabels(fieldLabels);
  return fieldLabels
    .map(fieldLabel => {
      if (
        fieldLabelIsPartOfDateRangeField(fieldLabel) &&
        dateRangeFieldLabelPairs[extractDateRangeFieldLabel(fieldLabel)]
      ) {
        if (fieldLabelIsDateRangeStartField(fieldLabel)) {
          return extractDateRangeFieldLabel(fieldLabel);
        }
        // filter out the *.EndDate fields
        return null;
      }
      return fieldLabel;
    })
    .filter(isTruthy);
};

// Like mergeDateRangeFieldNames, but only merges a `<x>_start_date` / `<x>_end_date`
// pair when both fields' labels also follow the `<x>.StartDate` / `<x>.EndDate`
// convention. Use this when a name-only merge would produce false positives on
// fields whose names coincidentally end in `_start_date` / `_end_date` but are
// not part of a real date range field (e.g. `change_history_start_date`).
export const mergeDateRangeFieldNamesWithLabelValidation = (
  fieldNames: string[],
  labelByName: Map<string, string>
): string[] => {
  const namePairs = getDateRangeFieldNamePairsFromFieldNames(fieldNames);
  return fieldNames.reduce<string[]>((mergedFields, fieldName) => {
    if (!fieldNameIsPartOfDateRangeField(fieldName)) {
      mergedFields.push(fieldName);
      return mergedFields;
    }
    const baseName = extractDateRangeFieldName(fieldName);
    const pair = namePairs[baseName];
    const startLabel = pair && labelByName.get(pair.start);
    const endLabel = pair && labelByName.get(pair.end);
    const labelsFormARealDateRange =
      startLabel !== undefined &&
      endLabel !== undefined &&
      fieldLabelIsDateRangeStartField(startLabel) &&
      fieldLabelIsDateRangeEndField(endLabel);
    if (!labelsFormARealDateRange) {
      mergedFields.push(fieldName);
      return mergedFields;
    }
    if (fieldNameIsDateRangeStartField(fieldName)) {
      mergedFields.push(baseName);
    }
    return mergedFields;
  }, []);
};

// You can use this function if you have a list of field names that you know all belong to the same component/reference type
export const mergeDateRangeFieldNames = (fieldNames: string[]): string[] => {
  const dateRangeFieldNamesPairs =
    getDateRangeFieldNamePairsFromFieldNames(fieldNames);
  return fieldNames
    .map(fieldName => {
      if (
        fieldNameIsPartOfDateRangeField(fieldName) &&
        dateRangeFieldNamesPairs[extractDateRangeFieldName(fieldName)]
      ) {
        if (fieldNameIsDateRangeStartField(fieldName)) {
          return extractDateRangeFieldName(fieldName);
        }
        // filter out the *._end_date fields
        return null;
      }
      return fieldName;
    })
    .filter(isTruthy);
};

const fieldToDateRangeField = (
  field: APIFieldAttributes
): APIFieldAttributes => ({
  ...field,
  label: extractDateRangeFieldLabel(field.label),
  name: createCombinedDateRangeFieldNames(
    extractDateRangeFieldName(field.name)
  ),
  type: getDateRangeType(field.type),
});

export const pairDateRangeFields = (
  fields: APIFieldAttributes[]
): APIFieldAttributes[] => {
  const result: APIFieldAttributes[] = [];
  const startFieldsByName = new Map<string, APIFieldAttributes>();
  const endFieldsByName = new Map<string, APIFieldAttributes>();

  fields.forEach(field => {
    if (!isPartOfDateRangeField(field)) {
      result.push(field);
      return;
    }

    const dateRangeFieldName = extractDateRangeFieldName(field.name);
    const isStart = fieldNameIsDateRangeStartField(field.name);

    if (isStart) {
      startFieldsByName.set(dateRangeFieldName, field);
    } else {
      endFieldsByName.set(dateRangeFieldName, field);
    }
  });

  startFieldsByName.forEach((startField, dateRangeFieldName) => {
    const endField = endFieldsByName.get(dateRangeFieldName);

    if (endField) {
      result.push(fieldToDateRangeField(startField));
      endFieldsByName.delete(dateRangeFieldName);
    } else {
      result.push(startField);
    }
  });

  endFieldsByName.forEach(endField => {
    result.push(endField);
  });

  return result;
};
